import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { sharedStyles } from '../styles/shared.js';
import { cartState, type Cart, getProductImageDisplay, resolveProductImageUrl } from '@landi/store-core';

/**
 * Mini cart icon for navbar with item count badge. Clicking the icon navigates to the cart page.
 * 
 * @element landi-cart-icon
 */
@customElement('landi-cart-icon')
export class LandiCartIcon extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: inline-block;
      }
      
      .cart-icon-wrapper {
        position: relative;
        padding: 0.5rem;
        border-radius: var(--landi-radius);
        transition: background var(--landi-transition);
      }
      
      .cart-icon-wrapper:hover {
        background: var(--landi-background);
      }

      .cart-icon-link {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: inherit;
        text-decoration: none;
        cursor: pointer;
      }
      
      .cart-icon {
        width: 24px;
        height: 24px;
        color: var(--landi-text);
        transition: color 200ms ease;
      }
      
      .cart-icon-wrapper:hover .cart-icon {
        color: var(--landi-primary);
      }
      
      .cart-badge {
        position: absolute;
        top: 0;
        right: 0;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        font-size: 0.688rem;
        font-weight: 700;
        line-height: 18px;
        text-align: center;
        color: #ffffff;
        background: var(--landi-primary);
        border-radius: 9999px;
        transform: translate(25%, -25%);
      }
      
      .mini-cart {
        position: absolute;
        top: 100%;
        right: 0;
        margin-top: 0.5rem;
        width: 320px;
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius-lg);
        box-shadow: var(--landi-shadow);
        z-index: 100;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-10px);
        transition: all 0.2s ease;
      }
      
      .cart-icon-wrapper:hover .mini-cart,
      .mini-cart:hover {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }
      
      .mini-cart-header {
        padding: 1rem;
        border-bottom: 1px solid var(--landi-border);
        font-weight: 600;
      }
      
      .mini-cart-items {
        max-height: 280px;
        overflow-y: auto;
      }
      
      .mini-cart-item {
        display: flex;
        gap: 0.75rem;
        padding: 0.75rem 1rem;
        border-bottom: 1px solid var(--landi-border);
      }
      
      .mini-cart-item:last-child {
        border-bottom: none;
      }
      
      .item-image {
        width: 48px;
        height: 48px;
        object-fit: cover;
        border-radius: var(--landi-radius);
        background: var(--landi-surface-elevated);
      }
      
      .item-details {
        flex: 1;
        min-width: 0;
      }
      
      .item-name {
        font-size: 0.875rem;
        font-weight: 500;
        margin: 0 0 0.25rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .item-variant {
        font-size: 0.75rem;
        color: var(--landi-text-muted);
        margin: 0 0 0.25rem;
      }
      
      .item-price {
        font-size: 0.813rem;
        color: var(--landi-primary);
        font-weight: 500;
      }
      
      .mini-cart-footer {
        padding: 1rem;
        border-top: 1px solid var(--landi-border);
      }
      
      .subtotal {
        display: flex;
        justify-content: space-between;
        margin-bottom: 1rem;
        font-weight: 600;
      }
      
      .subtotal-value {
        color: var(--landi-primary);
      }
      
      .view-cart-btn {
        display: block;
        width: 100%;
        padding: 0.75rem;
        text-align: center;
        background: var(--landi-text);
        color: #ffffff;
        font-weight: 700;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        border: none;
        border-radius: var(--landi-radius);
        cursor: pointer;
        text-decoration: none;
        transition: background var(--landi-transition);
      }
      
      .view-cart-btn:hover {
        background: var(--landi-primary);
      }
      
      .empty-cart {
        padding: 2rem 1rem;
        text-align: center;
        color: var(--landi-text-muted);
      }
    `,
  ];

  @property({ type: String, attribute: 'cart-href' }) cartHref = 'cart.html';
  @state() private cart: Cart = { items: [], subtotal: 0, tax: 0, shipping: 0, discount: 0, total: 0, itemCount: 0 };

  private unsubscribe?: () => void;

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = cartState.subscribe((cart) => {
      this.cart = cart;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
  }

  private formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  }

  render() {
    return html`
      <div class="cart-icon-wrapper">
        <a href=${this.cartHref} class="cart-icon-link" aria-label="View shopping cart">
          <svg class="cart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="m1 1 4 4 2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          ${this.cart.itemCount > 0 ? html`
            <span class="cart-badge">${this.cart.itemCount}</span>
          ` : ''}
        </a>
        <div class="mini-cart">
          <div class="mini-cart-header">
            Shopping Cart (${this.cart.itemCount})
          </div>
          
          ${this.cart.items.length > 0 ? html`
            <div class="mini-cart-items">
              ${this.cart.items.map(item => {
                const imageDisplay = getProductImageDisplay({
                  url: resolveProductImageUrl(item.image),
                  name: item.name,
                  sku: item.sku,
                  theme: 'light',
                });
                return html`
                <div class="mini-cart-item">
                  <img 
                    class="item-image" 
                    src=${imageDisplay.src}
                    alt=${item.name}
                    .onerror=${imageDisplay.onError}
                  />
                  <div class="item-details">
                    <p class="item-name">${item.name}</p>
                    ${item.variantName ? html`
                      <p class="item-variant">${item.variantName}</p>
                    ` : ''}
                    <p class="item-price">${item.quantity} × ${this.formatPrice(item.price)}</p>
                  </div>
                </div>
              `;
              })}
            </div>
            
            <div class="mini-cart-footer">
              <div class="subtotal">
                <span>Subtotal</span>
                <span class="subtotal-value">${this.formatPrice(this.cart.subtotal)}</span>
              </div>
              <a href=${this.cartHref} class="view-cart-btn">View Cart</a>
            </div>
          ` : html`
            <div class="empty-cart">
              Your cart is empty
            </div>
          `}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-cart-icon': LandiCartIcon;
  }
}
